import { useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { getCategory, categories } from "@/mocks/categories";
import { getWorksByCategory } from "@/mocks/works";
import WorkCard from "@/components/feature/WorkCard";
import Breadcrumb from "@/components/feature/Breadcrumb";
import { usePageMeta } from "@/hooks/usePageMeta";

type SortKey = "heat" | "updated" | "rating";
type StatusFilter = "all" | "连载中" | "已完结";

export default function Category() {
  const { slug = "" } = useParams();
  const category = getCategory(slug);
  const [status, setStatus] = useState<StatusFilter>("all");
  const [sort, setSort] = useState<SortKey>("heat");

  usePageMeta(
    category
      ? `火影同人${category.name}类作品大全 - ${category.tagline}`
      : "分类未找到 - 火影同人动漫",
    category
      ? `汇总火影同人${category.name}类作品，${category.desc} 快速筛选按热度、评分或更新时间排序的同人作品。`
      : "没有找到对应的作品分类。",
  );

  const list = useMemo(() => {
    if (!category) return [];
    let l = getWorksByCategory(category.slug);
    if (status !== "all") l = l.filter((w) => w.status === status);
    const s = [...l];
    if (sort === "heat") s.sort((a, b) => b.heat - a.heat);
    else if (sort === "rating") s.sort((a, b) => b.rating - a.rating);
    else s.sort((a, b) => b.updatedAt.localeCompare(a.updatedAt));
    return s;
  }, [category, status, sort]);

  if (!category) {
    return (
      <div className="container-x py-24 text-center">
        <i className="ri-emotion-sad-line text-4xl text-background-300" />
        <h1 className="mt-4 font-heading text-xl font-black text-foreground-950">分类未找到</h1>
        <p className="mt-2 text-sm text-foreground-600">你访问的作品分类不存在或已被移除。</p>
      </div>
    );
  }

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    name: `火影同人${category.name}类作品`,
    description: category.desc,
    inLanguage: "zh-CN",
    url: `https://chakra321.github.io/category/${category.slug}`,
  };

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }}
      />
      <section className="relative overflow-hidden bg-background-100">
        <div className="container-x py-12 md:py-16">
          <Breadcrumb crumbs={[{ label: `${category.name}分类` }]} />
          <div className="mt-6 flex flex-col gap-5 md:flex-row md:items-center">
            <span className="flex h-14 w-14 shrink-0 items-center justify-center rounded-lg bg-primary-500 text-background-50">
              <i className={`${category.icon} text-2xl`} />
            </span>
            <div>
              <h1 className="font-heading text-2xl font-black text-foreground-950 md:text-3xl">
                {category.name}类火影同人
              </h1>
              <p className="mt-2 max-w-2xl text-sm leading-relaxed text-foreground-600">
                {category.desc} <span className="text-primary-600">{category.tagline}</span>
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="container-x py-10 md:py-14">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div className="flex flex-wrap gap-2">
            {(["all", "连载中", "已完结"] as StatusFilter[]).map((s) => (
              <button
                key={s}
                type="button"
                onClick={() => setStatus(s)}
                className={`rounded-full px-4 py-1.5 text-sm whitespace-nowrap transition-colors ${
                  status === s
                    ? "bg-primary-500 text-background-50"
                    : "bg-background-100 text-foreground-600 hover:bg-background-200"
                }`}
              >
                {s === "all" ? "全部" : s}
              </button>
            ))}
          </div>

          <div className="flex items-center gap-1 rounded-full bg-background-100 p-1">
            {(
              [
                ["heat", "热度"],
                ["rating", "评分"],
                ["updated", "最新"],
              ] as [SortKey, string][]
            ).map(([key, label]) => (
              <button
                key={key}
                type="button"
                onClick={() => setSort(key)}
                className={`rounded-full px-4 py-1 text-sm whitespace-nowrap transition-colors ${
                  sort === key
                    ? "bg-background-50 font-medium text-primary-700"
                    : "text-foreground-500 hover:text-foreground-800"
                }`}
              >
                {label}
              </button>
            ))}
          </div>
        </div>

        <p className="mt-6 text-xs text-foreground-500">
          共找到 {list.length} 部{category.name}类火影同人作品
        </p>

        {list.length > 0 ? (
          <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {list.map((w) => (
              <WorkCard key={w.id} work={w} />
            ))}
          </div>
        ) : (
          <div className="mt-6 flex flex-col items-center justify-center rounded-lg border border-dashed border-background-300 bg-background-100 py-16 text-center">
            <i className="ri-inbox-line text-4xl text-background-300" />
            <p className="mt-4 text-sm text-foreground-600">该状态下暂无作品</p>
          </div>
        )}
      </section>

      <section className="border-t border-background-200 bg-background-100 py-10">
        <div className="container-x">
          <h2 className="font-heading text-lg font-bold text-foreground-950">其他剧情分类</h2>
          <div className="mt-4 flex flex-wrap gap-2">
            {categories
              .filter((c) => c.slug !== category.slug)
              .map((c) => (
                <a
                  key={c.slug}
                  href={`/category/${c.slug}`}
                  className="inline-flex items-center gap-1.5 rounded-full border border-background-300 bg-background-50 px-3.5 py-1.5 text-sm text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-700"
                >
                  <i className={`${c.icon}`} />
                  {c.name}类同人
                </a>
              ))}
          </div>
        </div>
      </section>
    </>
  );
}